import { generateAuthHeaders } from './wallet'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://earn.dogici.com/api/v1'

// Types for API responses
export interface RevenueSummaryItem {
  revenue_type: string
  chain: string
  total_revenue: number
  total_transactions: number
  first_transaction_date: string
  last_transaction_date: string
}

export interface DailyRevenueItem {
  date: string
  revenue_type: string
  chain: string
  total_amount: number
  transaction_count: number
}

export interface RevenueTransaction {
  id: number
  revenue_type: string
  chain: string
  amount: number
  original_amount: number
  commission_rate: number
  campaign_id?: number
  ad_session_id?: number
  user_id: number
  metadata?: {
    survey_trans_id?: string
    amount_usd?: number
  }
  created_at: string
}

export interface ApiResponse<T> {
  success: boolean
  data: T
  message: string
  error?: string
}

// Auth credentials stored in memory
let authCredentials: {
  privateKey: Uint8Array
  address: string
} | null = null

/**
 * Set authentication credentials for API calls
 */
export function setAuthCredentials(privateKey: Uint8Array, address: string): void {
  authCredentials = { privateKey, address }
}

/**
 * Clear authentication credentials
 */
export function clearAuthCredentials(): void {
  authCredentials = null
}

/**
 * Check if authenticated
 */
export function isAuthenticated(): boolean {
  return authCredentials !== null
}

/**
 * Make authenticated API request
 * Signs only the path (without query parameters) for authentication
 */
async function apiRequest<T>(
  path: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  if (!authCredentials) {
    throw new Error('Not authenticated')
  }

  // Extract path without query parameters for signing
  const pathWithoutQuery = path.split('?')[0]
  const signPath = `/api/v1${pathWithoutQuery}`

  const headers = await generateAuthHeaders(
    authCredentials.privateKey,
    authCredentials.address,
    signPath
  )

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  })

  const data = await response.json()

  if (!response.ok) {
    throw new Error(data.error || `Request failed: ${response.status}`)
  }

  return data
}

/**
 * Get revenue summary
 */
export async function getRevenueSummary(params?: {
  revenue_type?: string
  chain?: string
}): Promise<RevenueSummaryItem[]> {
  const searchParams = new URLSearchParams()
  if (params?.revenue_type) searchParams.set('revenue_type', params.revenue_type)
  if (params?.chain) searchParams.set('chain', params.chain)

  const query = searchParams.toString()
  const path = `/admin/revenue/summary${query ? `?${query}` : ''}`

  const response = await apiRequest<RevenueSummaryItem[]>(path)
  return response.data
}

/**
 * Get daily revenue
 */
export async function getDailyRevenue(params: {
  start_date: string
  end_date: string
  revenue_type?: string
  chain?: string
}): Promise<DailyRevenueItem[]> {
  const searchParams = new URLSearchParams({
    start_date: params.start_date,
    end_date: params.end_date,
  })
  if (params.revenue_type) searchParams.set('revenue_type', params.revenue_type)
  if (params.chain) searchParams.set('chain', params.chain)

  const path = `/admin/revenue/daily?${searchParams.toString()}`

  const response = await apiRequest<DailyRevenueItem[]>(path)
  return response.data
}

/**
 * Get revenue transactions
 */
export async function getTransactions(params?: {
  limit?: number
  offset?: number
  revenue_type?: string
  chain?: string
}): Promise<RevenueTransaction[]> {
  const searchParams = new URLSearchParams()
  if (params?.limit) searchParams.set('limit', params.limit.toString())
  if (params?.offset) searchParams.set('offset', params.offset.toString())
  if (params?.revenue_type) searchParams.set('revenue_type', params.revenue_type)
  if (params?.chain) searchParams.set('chain', params.chain)

  const query = searchParams.toString()
  const path = `/admin/revenue/transactions${query ? `?${query}` : ''}`

  const response = await apiRequest<RevenueTransaction[]>(path)
  return response.data
}

// System Airdrop Types
export interface SystemAirdropRecord {
  id: string
  executor_address: string
  amount_per_user: number
  message: string
  total_users_in_userbase: number
  users_credited: number
  users_skipped: number
  total_distributed: number
  created_at: string
}

export interface ExecuteAirdropRequest {
  amount: number      // in shibes
  message: string     // mandatory
}

export interface ExecuteAirdropResponse {
  airdrop_id: string
  total_users_in_userbase: number
  users_credited: number
  users_skipped: number
  amount_per_user: number
  total_distributed: number
}

export interface SystemAirdropHistoryResponse {
  records: SystemAirdropRecord[]
  total_count: number
  limit: number
  offset: number
}

/**
 * Execute a system airdrop to all users
 */
export async function executeSystemAirdrop(data: ExecuteAirdropRequest): Promise<ExecuteAirdropResponse> {
  const path = '/system-airdrop/execute'

  const response = await apiRequest<ExecuteAirdropResponse>(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
  return response.data
}

/**
 * Get system airdrop history
 */
export async function getSystemAirdropHistory(params?: {
  limit?: number
  offset?: number
}): Promise<SystemAirdropHistoryResponse> {
  const searchParams = new URLSearchParams()
  if (params?.limit) searchParams.set('limit', params.limit.toString())
  if (params?.offset) searchParams.set('offset', params.offset.toString())

  const query = searchParams.toString()
  const path = `/system-airdrop/history${query ? `?${query}` : ''}`

  const response = await apiRequest<SystemAirdropHistoryResponse>(path)
  return response.data
}
